import moment from 'moment';
import { useState } from 'react';
import { useSnackbar } from 'notistack';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import CardContent from '@mui/material/CardContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';

import { AiCoverage, useSetAiLayerMutation, useIndexAiLayerMutation, useGetAiLayerStatusQuery } from 'src/services/admin/ai-layer.service';

import Iconify from 'src/components/iconify';

const count = (n: number) => n.toLocaleString('en-AU');

function CoverageRow({ label, coverage }: { label: string; coverage: AiCoverage }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2">
        {coverage.indexed === null ? 'unknown' : count(coverage.indexed)} of {count(coverage.total)} in AI search
      </Typography>
    </Stack>
  );
}

/**
 * The AI layer switch on an agency's System Dashboard page (JT1-1820). JobTetris admins only: the
 * page itself is restricted, and so are the endpoints. One switch for every AI feature; turning it
 * on does not index anything, which is what Index now is for.
 */
export default function AiLayerCard({ organizationId }: { organizationId: string }) {
  const { enqueueSnackbar } = useSnackbar();
  const { currentData: status, isLoading, refetch } = useGetAiLayerStatusQuery(organizationId, { skip: !organizationId });
  const [setAiLayer, { isLoading: switching }] = useSetAiLayerMutation();
  const [indexAiLayer, { isLoading: indexing }] = useIndexAiLayerMutation();
  const [confirm, setConfirm] = useState<null | 'on' | 'off' | 'index'>(null);

  const run = async () => {
    try {
      if (confirm === 'index') {
        await indexAiLayer(organizationId).unwrap();
        enqueueSnackbar('Indexing queued. The counts go up as the worker gets through it.', { variant: 'success' });
      } else if (confirm) {
        await setAiLayer({ organizationId, enabled: confirm === 'on' }).unwrap();
        enqueueSnackbar(`AI layer switched ${confirm}.`, { variant: 'success' });
      }
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message ?? 'That did not work. Please try again.', { variant: 'error' });
    }
    setConfirm(null);
  };

  return (
    <Card sx={{ mt: 3 }}>
      <CardHeader
        title="AI layer"
        subheader="Whole database sourcing, applicant scoring, note suggestions, suitable jobs, and keeping AI search up to date"
        avatar={<Iconify icon="mdi:robot-outline" width={24} />}
        action={
          status && (
            <FormControlLabel
              control={<Switch checked={status.enabled} disabled={switching} onChange={(e) => setConfirm(e.target.checked ? 'on' : 'off')} />}
              label={status.enabled ? 'On' : 'Off'}
              labelPlacement="start"
            />
          )
        }
      />
      <CardContent>
        {isLoading && <Skeleton variant="rounded" height={120} />}

        {status && (
          <Stack spacing={2}>
            <Stack spacing={0.75}>
              <CoverageRow label="Candidates" coverage={status.coverage.candidates} />
              <CoverageRow label="Contacts" coverage={status.coverage.contacts} />
              <CoverageRow label="Companies" coverage={status.coverage.companies} />
              <Typography variant="caption" color="text.disabled">
                Records with nothing for the AI to read (no resume, notes, role or company) are never in AI search.
              </Typography>
            </Stack>

            <Stack direction="row" gap={1}>
              <LoadingButton size="small" variant="outlined" loading={indexing} onClick={() => setConfirm('index')} startIcon={<Iconify icon="solar:refresh-bold" width={16} />}>
                Index now
              </LoadingButton>
              <Button size="small" color="inherit" onClick={() => refetch()}>Refresh counts</Button>
            </Stack>

            {status.changes.length > 0 && (
              <>
                <Divider />
                <Stack spacing={0.5}>
                  {status.changes.map((change, i) => (
                    <Typography key={i} variant="caption" color="text.secondary">
                      Switched {change.enabled ? 'on' : 'off'} by {change.by ?? 'unknown'}, {moment(change.at).format('D MMM YYYY, h:mm a')}
                    </Typography>
                  ))}
                </Stack>
              </>
            )}
          </Stack>
        )}
      </CardContent>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {confirm === 'index' && 'Index this agency now?'}
          {confirm === 'on' && 'Switch the AI layer on?'}
          {confirm === 'off' && 'Switch the AI layer off?'}
        </DialogTitle>
        <DialogContent>
          {confirm === 'index' && (
            <Typography variant="body2">
              Every candidate, contact and company is read and indexed again. A large agency takes up to an hour and a few
              dollars of AI cost. Results may look thin while it runs.
            </Typography>
          )}
          {confirm === 'on' && (
            <Stack spacing={1.5}>
              <Typography variant="body2">
                The agency gets every AI feature straight away, and its use is paid by JobTetris.
              </Typography>
              {status && (status.coverage.candidates.indexed ?? 0) === 0 && status.coverage.candidates.total > 0 && (
                <Alert severity="warning">Nothing of this agency is in AI search yet. Use Index now after switching on.</Alert>
              )}
            </Stack>
          )}
          {confirm === 'off' && (
            <Typography variant="body2">
              Every AI feature and all background AI work for this agency stop straight away. Its AI search is kept, so
              switching back on works immediately. Values already filled in and suggestions already accepted stay.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <LoadingButton variant="contained" color={confirm === 'off' ? 'error' : 'primary'} loading={switching || indexing} onClick={run}>
            {confirm === 'index' ? 'Index now' : `Switch ${confirm}`}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
